import { useState, type Dispatch, type SetStateAction } from "react";

/**
 * `resetKey` 가 바뀌면 `resetTo` 로 돌아가는 state.
 *
 * 「prop 이 바뀌면 고른 값을 지운다」를 `useEffect(() => setX(resetTo), [key])` 로 쓰면
 * react-hooks `set-state-in-effect` 위반이고, **낡은 값으로 한 번 그려 커밋한 뒤** 다시 그린다.
 * 여기서는 React 문서의 「prop 이 바뀔 때 state 조정하기」대로 직전 key 를 state 로 들고 렌더 중에
 * 비교한다 — 바뀐 렌더는 커밋 전에 버려지고 곧바로 다시 그려져서 낡은 값이 화면에 나가지 않는다.
 * (https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes)
 *
 * 지우는 것이지 key 별로 기억하는 것이 아니다 — key 가 A → B → A 로 돌아와도 A 에서 골랐던 값은
 * 되살아나지 않는다(effect 로 지우던 동작과 같다).
 *
 * ⚠️ `resetKey` 는 **원시값만** 받는다(타입으로 막았다). 렌더마다 새로 만드는 객체·배열을 넘기면 매
 * 렌더가 「바뀜」이라 무한 재렌더(React 「Too many re-renders」)가 난다.
 * ⚠️ `resetTo` 는 값이다 — 함수를 넘기면 `useState`·setter 가 초기화 함수·updater 로 읽는다. 함수를
 * 담는 state 에는 쓰지 않는다.
 *
 * 도입: `src/pages/shop/buy-tactsuit/[slug].tsx` 의 세트/단품 선택 — `?pack=` 이 바뀌면 고른 값을
 * 지운다(#6010 이 넣은 effect 를 대체). 기준선(`eslint-suppressions.json`)의 같은 모양 error 도 이걸로
 * 고칠 수 있다 — `docs/eslint-policy.md` §4.1.
 */
export function useResettableState<T>(
  resetTo: T,
  resetKey: string | number | boolean | null | undefined,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(resetTo);
  const [key, setKey] = useState(resetKey);
  if (!Object.is(key, resetKey)) {
    setKey(resetKey);
    setValue(resetTo);
    // 이 렌더는 커밋 전에 버려진다 — 그 사이에도 낡은 값을 돌려주지 않는다.
    return [resetTo, setValue];
  }
  return [value, setValue];
}
